import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  api,
  ApiError,
  type CaseDetailResponse,
  type CaseDetailRow,
  type SubcontractorListItem,
  type BranchRef,
} from '../lib/api'
import type { CaseStatus, FileKind, Severity } from '../../shared/types'
import { SeverityBadge, StatusBadge } from '../components/Badges'
import { PhoneLink, AddressLink } from '../components/links'
import { compressPhoto, isAllowedAttachment, ATTACHMENT_ACCEPT } from '../lib/photo'
import { useAuth } from '../lib/auth'
import { formatDate, formatUtcDate, formatDateTime, formatMoney } from '../lib/format'

const STATUSES: CaseStatus[] = ['open', 'in_progress', 'closed']

export function CaseDetailPage() {
  const { caseId = '' } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const canDelete = user?.role !== 'company_user' // admins only
  const [data, setData] = useState<CaseDetailResponse | null>(null)
  const [subs, setSubs] = useState<SubcontractorListItem[]>([])
  const [branches, setBranches] = useState<BranchRef[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)
  const [mergeTarget, setMergeTarget] = useState('')
  const [mergeBusy, setMergeBusy] = useState(false)
  const [mergeErr, setMergeErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [detail, subList, branchList] = await Promise.all([
        api.getCase(caseId),
        api.listSubcontractors(),
        api.listBranches().catch(() => ({ branches: [] as BranchRef[] })),
      ])
      setData(detail)
      setSubs(subList.subcontractors)
      setBranches(branchList.branches)
    } catch {
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <div style={{ color: 'var(--gray)' }}>Loading…</div>
  if (notFound || !data) return <div className="page-title">Case not found</div>

  const k = data.case
  const availableSubs = subs.filter((s) => !data.subcontractors.some((a) => a.id === s.id))

  async function setStatus(status: CaseStatus) {
    await api.updateCase(caseId, { status })
    await load()
  }
  async function changeBranch(branchId: string) {
    await api.updateCase(caseId, { branch_id: branchId || null })
    await load()
  }
  async function deleteCase() {
    if (!data) return
    if (!window.confirm(`Permanently delete case ${data.case.case_number}? This removes its photos, files, and comments and cannot be undone.`)) return
    await api.deleteCase(caseId)
    navigate('/app/open')
  }
  async function doMerge() {
    const target = mergeTarget.trim()
    if (!target || !data) return
    setMergeBusy(true)
    setMergeErr(null)
    try {
      const res = await api.mergeCase(caseId, target)
      navigate(`/app/cases/${res.target_id}`)
    } catch (e) {
      setMergeErr(
        e instanceof ApiError && (e.code === 'target_not_found' || e.code === 'same_case')
          ? e.code === 'same_case'
            ? "That's the same case."
            : `No case found with number "${target}".`
          : 'Could not merge. Please try again.',
      )
    } finally {
      setMergeBusy(false)
    }
  }
  async function addSub(subId: string) {
    await api.addCaseSubcontractor(caseId, subId)
    await load()
  }
  async function removeSub(subId: string) {
    await api.removeCaseSubcontractor(caseId, subId)
    await load()
  }
  async function deletePhoto(photoId: string) {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return
    await api.deleteCasePhoto(caseId, photoId)
    await load()
  }

  return (
    <div>
      <Link to={`/app/${k.status === 'closed' ? 'closed' : k.status === 'in_progress' ? 'in-progress' : 'open'}`} className="link-btn">
        ← Back to cases
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 4px' }}>
        <div className="page-title" style={{ margin: 0 }}>
          Case {k.case_number}
        </div>
        <StatusBadge status={k.status} />
        <SeverityBadge severity={k.severity} />
        {canDelete && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 14 }}>
            <button className="link-btn" onClick={() => { setMerging((v) => !v); setMergeErr(null) }}>
              Merge
            </button>
            <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void deleteCase()}>
              Delete case
            </button>
          </span>
        )}
      </div>

      {canDelete && merging && (
        <div className="card" style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>Merge this case into:</span>
          <input
            value={mergeTarget}
            onChange={(e) => setMergeTarget(e.target.value)}
            placeholder="e.g. FIC-000123"
            style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, font: 'inherit' }}
          />
          <button className="btn btn-primary" disabled={mergeBusy || !mergeTarget.trim()} onClick={() => void doMerge()}>
            {mergeBusy ? 'Merging…' : 'Merge'}
          </button>
          <button className="link-btn" onClick={() => setMerging(false)}>Cancel</button>
          <span style={{ flexBasis: '100%', fontSize: 12, color: 'var(--gray)' }}>
            This case's photos, files, comments, and subcontractors move into the case you enter, then this case is deleted.
          </span>
          {mergeErr && <span style={{ flexBasis: '100%', color: 'var(--red)', fontSize: 13 }}>{mergeErr}</span>}
        </div>
      )}
      <p className="page-sub">
        Opened {formatDateTime(k.opened_at)}
        {k.closed_at ? ` · Closed ${formatDateTime(k.closed_at)}` : ''}
      </p>
      {(branches.length > 0 || data.branch) && (
        <p className="page-sub" style={{ marginTop: -6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Branch:</span>
          {canDelete && branches.length > 0 ? (
            <select
              value={data.branch?.id ?? ''}
              onChange={(e) => void changeBranch(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 6, font: 'inherit' }}
            >
              <option value="">— None —</option>
              {branches
                .filter((b) => b.active === 1 || b.id === data.branch?.id)
                .map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
            </select>
          ) : (
            <strong>{data.branch?.name ?? '—'}</strong>
          )}
        </p>
      )}

      <div className="case-grid">
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Customer + damage (view or edit) */}
          {editing ? (
            <CaseEditForm k={k} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await load() }} />
          ) : (
            <>
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <SectionTitle>Customer information</SectionTitle>
                  <button className="link-btn" style={{ marginLeft: 'auto' }} onClick={() => setEditing(true)}>
                    Edit
                  </button>
                </div>
                <Detail label="Name" value={k.customer_name} />
                <Detail label="Phone" value={<PhoneLink phone={k.customer_phone} />} />
                <Detail label="Address" value={<AddressLink address={k.address} />} />
                {k.customer_email && <Detail label="Email" value={k.customer_email} />}
              </div>

              <div className="card">
                <SectionTitle>Damage details</SectionTitle>
                <Detail label="Technician" value={k.technician_name} />
                <Detail label="Tech phone" value={<PhoneLink phone={k.technician_phone} />} />
                <Detail label="Supervisor" value={k.supervisor_name} />
                <Detail label="Supervisor phone" value={<PhoneLink phone={k.supervisor_phone} />} />
                <Detail label="Incident date" value={formatDate(k.incident_date)} />
                <Detail label="Description" value={k.description} />
              </div>
            </>
          )}

          {/* Photos */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: data.photos.length ? 12 : 0 }}>
              <SectionTitle>Photos &amp; files ({data.photos.length})</SectionTitle>
              <span style={{ marginLeft: 'auto' }}>
                <AddPhotos caseId={caseId} onAdded={load} />
              </span>
            </div>
            {data.photos.length === 0 ? (
              <Empty>No photos or files yet.</Empty>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {data.photos.map((p) => (
                  <div key={p.id} style={{ position: 'relative' }}>
                    <a href={api.photoUrl(caseId, p.id)} target="_blank" rel="noreferrer">
                      {p.is_pdf ? (
                        <div style={{ width: 120, height: 120, borderRadius: 8, border: '1px solid var(--line)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg)', color: 'var(--gray)' }}>
                          <span style={{ fontSize: 34 }}>📄</span>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>PDF</span>
                        </div>
                      ) : (
                        <img
                          src={api.photoUrl(caseId, p.id)}
                          alt="Damage"
                          style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', display: 'block' }}
                        />
                      )}
                    </a>
                    {canDelete && (
                      <button
                        className="photo-del"
                        title="Delete photo"
                        onClick={() => void deletePhoto(p.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Costs / files */}
          <CostsSection caseId={caseId} data={data} onChange={load} canDelete={canDelete} />

          {/* Comments */}
          <CommentsSection caseId={caseId} data={data} onChange={load} />
        </div>

        {/* Right rail: status + assignment */}
        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <div className="card">
            <SectionTitle>Status</SectionTitle>
            <div style={{ display: 'grid', gap: 8 }}>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  className="btn"
                  onClick={() => void setStatus(s)}
                  disabled={s === k.status}
                  style={{
                    justifyContent: 'flex-start',
                    border: '1px solid var(--line)',
                    background: s === k.status ? 'var(--bg)' : 'var(--surface)',
                    fontWeight: s === k.status ? 700 : 500,
                  }}
                >
                  {s === k.status ? '● ' : ''}
                  {s === 'in_progress' ? 'In Progress' : s[0].toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <SectionTitle>Subcontractors</SectionTitle>
            {data.subcontractors.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 10 }}>None assigned yet.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                {data.subcontractors.map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Link to={`/app/subcontractors/${s.id}`} style={{ fontWeight: 600 }}>{s.name}</Link>
                      <div style={{ fontSize: 12, color: 'var(--gray)' }}>
                        {s.contact_person ?? ''} {s.phone && <PhoneLink phone={s.phone} />}
                      </div>
                    </div>
                    <button className="link-btn" style={{ color: 'var(--red)' }} onClick={() => void removeSub(s.id)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            {availableSubs.length > 0 ? (
              <select value="" onChange={(e) => e.target.value && void addSub(e.target.value)} style={selectStyle}>
                <option value="">+ Add subcontractor…</option>
                {availableSubs.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : subs.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--gray)' }}>Add subcontractors under Subcontractors first.</p>
            ) : (
              <p style={{ fontSize: 12, color: 'var(--gray)' }}>All subcontractors are assigned.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CostsSection({
  caseId,
  data,
  onChange,
  canDelete,
}: {
  caseId: string
  data: CaseDetailResponse
  onChange: () => Promise<void>
  canDelete: boolean
}) {
  async function deleteFile(fileId: string) {
    if (!window.confirm('Delete this document? This cannot be undone.')) return
    await api.deleteCaseFile(caseId, fileId)
    await onChange()
  }
  const [kind, setKind] = useState<FileKind>('invoice')
  const [amount, setAmount] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [fileKey, setFileKey] = useState(0) // bump to reset the file input
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!file) {
      setError('Choose a file to upload.')
      return
    }
    setError(null)
    setBusy(true)
    const fd = new FormData()
    fd.set('kind', kind)
    if (amount) fd.set('amount', amount)
    fd.set('file', file)
    try {
      await api.uploadCaseFile(caseId, fd)
      setAmount('')
      setFile(null)
      setFileKey((n) => n + 1)
      await onChange()
    } catch {
      setError('Upload failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <SectionTitle>Costs &amp; documents</SectionTitle>
        <div style={{ fontWeight: 700 }}>Total: {formatMoney(data.cost_total)}</div>
      </div>

      {data.files.length === 0 ? (
        <Empty>No documents yet.</Empty>
      ) : (
        <div className="table-wrap" style={{ marginBottom: 12 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount</th>
              <th>Uploaded</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.files.map((f) => (
              <tr key={f.id}>
                <td style={{ textTransform: 'capitalize' }}>{f.kind}</td>
                <td>{formatMoney(f.amount)}</td>
                <td>{formatUtcDate(f.uploaded_at)}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <a href={api.fileUrl(caseId, f.id)} target="_blank" rel="noreferrer">
                    View
                  </a>
                  {canDelete && (
                    <button
                      className="link-btn"
                      style={{ color: 'var(--red)', marginLeft: 12 }}
                      onClick={() => void deleteFile(f.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select value={kind} onChange={(e) => setKind(e.target.value as FileKind)} style={selectStyle}>
          <option value="invoice">Invoice</option>
          <option value="receipt">Receipt</option>
          <option value="cost">Cost</option>
        </select>
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ ...selectStyle, width: 120 }}
        />
        <input key={fileKey} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Uploading…' : 'Add'}
        </button>
      </form>
      {error && <p className="form-error">{error}</p>}
    </div>
  )
}

function CommentsSection({
  caseId,
  data,
  onChange,
}: {
  caseId: string
  data: CaseDetailResponse
  onChange: () => Promise<void>
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    setBusy(true)
    try {
      await api.addComment(caseId, text.trim())
      setText('')
      await onChange()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <SectionTitle>Comments ({data.comments.length})</SectionTitle>
      <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
        {data.comments.length === 0 && <Empty>No comments yet.</Empty>}
        {data.comments.map((cm) => (
          <div key={cm.id} style={{ borderLeft: '3px solid var(--line)', paddingLeft: 10 }}>
            <div style={{ fontSize: 13 }}>{cm.body}</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>
              {cm.author ?? 'Unknown'} · {formatDateTime(cm.created_at)}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment…"
          style={{ ...selectStyle, flex: 1 }}
        />
        <button className="btn btn-primary" type="submit" disabled={busy}>
          Post
        </button>
      </form>
    </div>
  )
}

function CaseEditForm({
  k,
  onCancel,
  onSaved,
}: {
  k: CaseDetailRow
  onCancel: () => void
  onSaved: () => void | Promise<void>
}) {
  const [f, setF] = useState({
    customer_name: k.customer_name ?? '',
    customer_phone: k.customer_phone ?? '',
    customer_email: k.customer_email ?? '',
    address: k.address ?? '',
    technician_name: k.technician_name ?? '',
    technician_phone: k.technician_phone ?? '',
    supervisor_name: k.supervisor_name ?? '',
    supervisor_phone: k.supervisor_phone ?? '',
    incident_date: toLocalInput(k.incident_date),
    description: k.description ?? '',
    severity: (k.severity ?? '') as Severity | '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const set = (key: keyof typeof f, v: string) => setF((p) => ({ ...p, [key]: v }))

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.updateCase(k.id, {
        customer_name: f.customer_name,
        customer_phone: f.customer_phone,
        customer_email: f.customer_email,
        address: f.address,
        technician_name: f.technician_name,
        technician_phone: f.technician_phone,
        supervisor_name: f.supervisor_name,
        supervisor_phone: f.supervisor_phone,
        incident_date: f.incident_date || null,
        description: f.description,
        severity: f.severity || null,
      })
      await onSaved()
    } catch {
      setError('Could not save changes. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card" onSubmit={onSubmit}>
      <SectionTitle>Edit case details</SectionTitle>
      <div className="edit-grid">
        <Fld label="Customer name" v={f.customer_name} on={(v) => set('customer_name', v)} />
        <Fld label="Customer phone" v={f.customer_phone} on={(v) => set('customer_phone', v)} type="tel" />
        <Fld label="Customer email" v={f.customer_email} on={(v) => set('customer_email', v)} type="email" />
        <Fld label="Address" v={f.address} on={(v) => set('address', v)} />
        <Fld label="Technician" v={f.technician_name} on={(v) => set('technician_name', v)} />
        <Fld label="Technician phone" v={f.technician_phone} on={(v) => set('technician_phone', v)} type="tel" />
        <Fld label="Supervisor" v={f.supervisor_name} on={(v) => set('supervisor_name', v)} />
        <Fld label="Supervisor phone" v={f.supervisor_phone} on={(v) => set('supervisor_phone', v)} type="tel" />
        <div className="field" style={{ margin: 0 }}>
          <label>Incident date</label>
          <input type="datetime-local" value={f.incident_date} onChange={(e) => set('incident_date', e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Severity</label>
          <select value={f.severity} onChange={(e) => set('severity', e.target.value)} style={selectStyle}>
            <option value="">—</option>
            <option value="minor">Minor</option>
            <option value="moderate">Moderate</option>
            <option value="major">Major</option>
          </select>
        </div>
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <label>Description</label>
        <textarea value={f.description} onChange={(e) => set('description', e.target.value)} rows={3} style={{ ...selectStyle, width: '100%', resize: 'vertical' }} />
      </div>
      {error && <p className="form-error">{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
        <button type="button" className="btn" style={{ border: '1px solid var(--line)' }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

function Fld({ label, v, on, type = 'text' }: { label: string; v: string; on: (v: string) => void; type?: string }) {
  return (
    <div className="field" style={{ margin: 0 }}>
      <label>{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} />
    </div>
  )
}

// Stored incident_date -> datetime-local input value (YYYY-MM-DDTHH:MM).
function toLocalInput(value: string | null): string {
  if (!value) return ''
  const s = value.includes('T') ? value : value.replace(' ', 'T')
  return s.length >= 16 ? s.slice(0, 16) : `${s.slice(0, 10)}T00:00`
}

function AddPhotos({ caseId, onAdded }: { caseId: string; onAdded: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [key, setKey] = useState(0)
  async function onPick(files: FileList | null) {
    const list = Array.from(files ?? []).filter(isAllowedAttachment)
    if (list.length === 0) return
    setBusy(true)
    setErr(null)
    try {
      // Images are downscaled/re-encoded for reliable upload; PDFs pass through.
      const prepared = await Promise.all(list.map(compressPhoto))
      const fd = new FormData()
      for (const f of prepared) fd.append('photos', f)
      // Retry transient network failures (weak signal), not real server errors.
      let lastErr: unknown
      let ok = false
      for (let attempt = 0; attempt < 3 && !ok; attempt++) {
        try {
          await api.addCasePhotos(caseId, fd)
          ok = true
        } catch (e) {
          lastErr = e
          if (e instanceof ApiError) break
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)))
        }
      }
      if (!ok) throw lastErr
      await onAdded()
    } catch (e) {
      setErr(
        e instanceof ApiError && e.code === 'photo_too_large'
          ? 'A file is too large (max 8 MB each).'
          : 'Upload failed — check your connection and try again.',
      )
    } finally {
      setBusy(false)
      setKey((n) => n + 1)
    }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {err && <span style={{ color: 'var(--red)', fontSize: 12 }}>{err}</span>}
      <label className="link-btn" style={{ cursor: 'pointer' }}>
        {busy ? 'Uploading…' : '+ Add photos / files'}
        <input
          key={key}
          type="file"
          accept={ATTACHMENT_ACCEPT}
          multiple
          hidden
          onChange={(e) => void onPick(e.target.files)}
        />
      </label>
    </span>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div style={{ fontWeight: 700, marginBottom: 12 }}>{children}</div>
}
function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 14 }}>
      <div style={{ width: 110, color: 'var(--gray)', flexShrink: 0 }}>{label}</div>
      <div style={{ minWidth: 0, wordBreak: 'break-word' }}>{value ?? '—'}</div>
    </div>
  )
}
function Empty({ children }: { children: React.ReactNode }) {
  return <p style={{ color: 'var(--gray)', fontSize: 14 }}>{children}</p>
}

const selectStyle: React.CSSProperties = {
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
